import React from 'react';
import axios from 'axios';
import { Intent } from '@blueprintjs/core';
import {
  useAuthActions,
  useAuthOrganizationId,
  useSetGlobalErrors,
  useAuthToken,
} from './state';
import { getCookie } from '../utils';
import { AppToaster } from '@/components/AppToaster';

/**
 * Contrôles — filet de sécurité d'affichage : extrait un message lisible d'une
 * réponse 400 quelle que soit sa forme (BadRequestException string,
 * ValidationPipe array, ServiceError `errors[]`) pour qu'aucune erreur de
 * contrôle ne soit avalée silencieusement.
 */
function extractBadRequestMessage(data: any) {
  if (!data) return '';
  if (typeof data.message === 'string') return data.message;
  if (Array.isArray(data.message) && data.message.length) {
    const first = data.message[0];
    if (first?.constraints) return Object.values(first.constraints)[0];
    if (typeof first === 'string') return first;
    if (first?.message) return first.message;
  }
  if (Array.isArray(data.errors) && data.errors.length) {
    return data.errors[0]?.message || '';
  }
  return '';
}

export default function useApiRequest() {
  const setGlobalErrors = useSetGlobalErrors();
  const { setLogout } = useAuthActions();
  const currentLocale = getCookie('locale');

  // Authentication token.
  const token = useAuthToken();

  // Authentication organization id.
  // Cookie is the source of truth : il est écrit atomiquement au changement de
  // panel (useSwitchPanel) et survit à la navigation, alors que le state Redux
  // peut rester en retard juste après un changement de client. On lit donc le
  // cookie en priorité pour que l'en-tête `organization-id` ne se désynchronise
  // jamais du client réellement affiché (pill du switcher) — sinon les requêtes
  // partent vers le cabinet (ex. `leyona`) au lieu du client (ex. ANFA) et
  // renvoient des 400 « not found » / 500 en cascade.
  const organizationIdFromState = useAuthOrganizationId();
  const organizationId =
    getCookie('organization_id') || organizationIdFromState;

  const http = React.useMemo(() => {
    // Configure baseURL from environment
    const baseURL = process.env.REACT_APP_API_URL || '/api';

    // Axios instance.
    const instance = axios.create({
      baseURL,
    });

    // Request interceptors.
    instance.interceptors.request.use(
      (request) => {
        const locale = currentLocale;

        if (token) {
          request.headers['Authorization'] = `Bearer ${token}`;
        }
        if (organizationId) {
          console.log(
            '[useApiRequest] Adding organization-id header:',
            organizationId,
          );
          console.log('[useApiRequest] Request URL:', request.url);
          request.headers['organization-id'] = organizationId;
        } else {
          console.warn(
            '[useApiRequest] No organizationId available for request:',
            request.url,
          );
        }
        if (locale) {
          request.headers['Accept-Language'] = locale;
        }
        return request;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
    // Response interceptors.
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (!error.response) {
          // Network error or request was made but no response received
          console.error('Network error or no response:', error.message);
          return Promise.reject(error);
        }

        const { status, data } = error.response;

        if (status >= 500) {
          setGlobalErrors({ something_wrong: true });
        }
        if (status === 401) {
          setGlobalErrors({ session_expired: true });
          setLogout();
        }
        if (status === 403) {
          setGlobalErrors({ access_denied: true });
        }
        if (status === 429) {
          setGlobalErrors({ too_many_requests: true });
        }
        if (status === 400) {
          let handled = false;
          // Avis n°24 CNC — refus d'écriture sur exercice clos. NestJS BadRequest
          // returns { message: "Avis n°24 CNC: …" } which doesn't go through the
          // legacy `errors[]` envelope, so we sniff the message string.
          const msg =
            (typeof data?.message === 'string' && data.message) ||
            (Array.isArray(data?.message) && data.message.join(' ')) ||
            '';
          // Modal « période verrouillée » : détecté par le CODE stable
          // (FISCAL_PERIOD_LOCKED, indépendant de la langue) ; le sniff de
          // chaîne « Avis n°24 » reste un repli pour les anciens messages.
          if (
            data?.code === 'FISCAL_PERIOD_LOCKED' ||
            msg.includes('Avis n°24') ||
            msg.includes("refus d'écriture")
          ) {
            setGlobalErrors({
              fiscalPeriodLocked: { message: msg },
            });
            handled = true;
          }
          const lockedError = data.errors?.find(
            (error: any) => error.type === 'TRANSACTIONS_DATE_LOCKED',
          );
          if (lockedError) {
            setGlobalErrors({ transactionsLocked: { ...lockedError.data } });
            handled = true;
          }
          if (
            data.errors?.find(
              (e: any) => e.type === 'ORGANIZATION.SUBSCRIPTION.INACTIVE',
            )
          ) {
            setGlobalErrors({ subscriptionInactive: true });
            handled = true;
          }
          if (data.errors?.find((e: any) => e.type === 'USER_INACTIVE')) {
            setGlobalErrors({ userInactive: true });
            setLogout();
            handled = true;
          }

          // Filet de sécurité : surface tout 400 de contrôle non traité comme un
          // toast (au lieu d'un échec silencieux). Le message backend est déjà
          // traduit par les filtres i18n (ServiceErrorFilter / i18n-http-exception)
          // lorsqu'il s'agit d'une clé d'erreur.
          if (!handled) {
            const toastMsg = extractBadRequestMessage(data);
            if (toastMsg) {
              AppToaster.show({ message: toastMsg, intent: Intent.DANGER });
            }
          }
        }
        return Promise.reject(error);
      },
    );
    return instance;
  }, [token, organizationId, setGlobalErrors, setLogout]);

  return React.useMemo(
    () => ({
      http,

      get(resource: string, params?: any) {
        return http.get(resource, params);
      },

      post(resource: string, params?: any, config?: any) {
        return http.post(resource, params, config);
      },

      update(resource: string, slug: string | number, params?: any) {
        return http.put(`${resource}/${slug}`, params);
      },

      put(resource: string, params?: any) {
        return http.put(resource, params);
      },

      patch(resource: string, params?: any, config?: any) {
        return http.patch(resource, params, config);
      },

      delete(resource: string, params?: any) {
        return http.delete(resource, params);
      },
    }),
    [http],
  );
}

export function useAuthApiRequest() {
  const baseURL = process.env.REACT_APP_API_URL || '/api';

  const http = React.useMemo(() => {
    // Axios instance with baseURL for authentication requests
    return axios.create({
      baseURL,
    });
  }, [baseURL]);

  return React.useMemo(
    () => ({
      http,
      get(resource: string, params?: any) {
        return http.get(resource, params);
      },
      post(resource: string, params?: any, config?: any) {
        return http.post(resource, params, config);
      },
      update(resource: string, slug: string | number, params?: any) {
        return http.put(`${resource}/${slug}`, params);
      },
      put(resource: string, params?: any) {
        return http.put(resource, params);
      },
      patch(resource: string, params?: any, config?: any) {
        return http.patch(resource, params, config);
      },
      delete(resource: string, params?: any) {
        return http.delete(resource, params);
      },
    }),
    [http],
  );
}
